import { load as yamlLoad } from "js-yaml";

import type {
  PromptTemplate,
  ToolSchema
} from "../data/queries/lens-tester";
import { fetchLensContent, fetchLensData } from "../data/queries/lens-tester";

export interface Protocol {
  name: string;
  file: string;
  description: string;
  when_to_use: string;
}

export interface ProtocolIndex {
  name: string;
  description: string;
  protocols: Protocol[];
}

export interface CustomLensGroups {
  [group: string]: string[];
}

export interface ConsolidatedLensItem {
  category: string;
  filename: string;
  fullPath: string;
}

export interface LensData {
  industry: string;
  promptTemplate: PromptTemplate | null;
  toolSchema: ToolSchema | null;
  currentLens: string;
  protocolIndex: ProtocolIndex | null;
  customLensGroups: CustomLensGroups | null;
  consolidatedLensList: ConsolidatedLensItem[];
}

const validate = (value: string | null | undefined, msg: string) => {
  if (!value) {
    throw new Error(msg);
  }
};

const parseYaml = <T>(src: string | null, kind: string): T | null => {
  if (!src?.trim()) {
    return null;
  }
  try {
    return yamlLoad(src) as T;
  } catch {
    throw new Error(`Invalid ${kind} YAML`);
  }
};

interface BuildConsolidatedListParams {
  industry: string;
  tenantId: string;
  protocolIndex: ProtocolIndex | null;
  customLensGroups: CustomLensGroups | null;
}

const buildConsolidatedList = (params: BuildConsolidatedListParams): ConsolidatedLensItem[] => {
  const { industry, tenantId, protocolIndex, customLensGroups } = params;
  const items: ConsolidatedLensItem[] = [];

  if (protocolIndex) {
    protocolIndex.protocols.forEach(p => items.push({
      category: industry,
      filename: p.file,
      fullPath: `protocols/${industry}/${p.file}`
    }));
  }

  if (customLensGroups) {
    Object.entries(customLensGroups).forEach(([group, files]) =>
      files.forEach(f => items.push({
        category: group,
        filename: f,
        fullPath: `custom-protocols/${tenantId}/${group}/${f}`
      }))
    );
  }

  return items;
};

export const getLensData = async (
  tenantId: string,
  lensName: string
): Promise<LensData> => {
  validate(tenantId, "No tenant ID");
  validate(lensName, "No lens name");

  const { success, data, error } = await fetchLensData(tenantId, lensName);

  if (!success) {
    throw new Error(error);
  }

  const protocolIndex = parseYaml<ProtocolIndex>(data.protocolIndex, "protocol index");
  const customLensGroups = parseYaml<CustomLensGroups>(data.customLensGroups, "custom lens");

  return {
    industry: data.industry,
    promptTemplate: data.promptTemplate,
    toolSchema: data.toolSchema,
    currentLens: data.currentLens ?? "",
    protocolIndex,
    customLensGroups,
    consolidatedLensList: buildConsolidatedList({
      industry: data.industry,
      tenantId,
      protocolIndex,
      customLensGroups
    })
  };
};

export const getLensContent = async (
  tenantId: string,
  fullPath: string
): Promise<string> => {
  validate(tenantId, "No tenant ID");
  validate(fullPath, "No full path");

  const { success, data, error } = await fetchLensContent(tenantId, fullPath);

  if (!success || !data) {
    throw new Error(error || "Failed to fetch lens content");
  }

  return data;
};

const decodeHtmlEntities = (str: string): string =>
  str
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, "\"")
    .replace(/&#x2F;/g, "/")
    .replace(/&#39;/g, "'");

const normalizeFilename = (filename: string): string =>
  decodeHtmlEntities(filename)
    .replace(/\.md$/, "")
    .replace(/[-_\s]+/g, "")
    .toLowerCase();

const findByFilename = (
  list: ConsolidatedLensItem[],
  filename: string
): ConsolidatedLensItem | undefined =>
  list.find(item => normalizeFilename(item.filename) === normalizeFilename(filename));

export const findLensItem = (
  lensData: LensData,
  protocol: string
): ConsolidatedLensItem | undefined => {
  const byFilename = findByFilename(lensData.consolidatedLensList, protocol);
  if (byFilename) {
    return byFilename;
  }

  const protocolItem = lensData.protocolIndex?.protocols.find(
    p => normalizeFilename(p.name) === normalizeFilename(protocol)
  );

  return protocolItem
    ? findByFilename(lensData.consolidatedLensList, protocolItem.file)
    : undefined;
};
